import {
  DefaultMetricOptions,
  Goal,
  GoalProgressCount,
  ID,
} from '@griply/types';

import { calculateMetricEntryValue } from './calculate-metric-entry-value';
import { getSubGoals } from './goals/get-sub-goals';

export type GetGoalCompletionOptions = {
  goals: Goal[];
  goalProgressCounts?: Map<ID, GoalProgressCount>;
};

export type GoalCompletion = {
  start: number;
  current: number;
  end: number;
};

export const getGoalCompletion = (
  goal: Goal,
  { goals, goalProgressCounts }: GetGoalCompletionOptions,
): GoalCompletion => {
  if (!goal.metric?.id) {
    return {
      start: 0,
      current: 0,
      end: Infinity,
    };
  }

  if (goal.metric?.id === DefaultMetricOptions.CompletedTasks) {
    // no live counts yet — report an unmeasurable target rather than a false 0%
    if (!goalProgressCounts) {
      return {
        start: 0,
        current: 0,
        end: Infinity,
      };
    }

    const { completed, total } = goalProgressCounts.get(goal.id) ?? {
      completed: 0,
      total: 0,
    };

    return {
      start: 0,
      current: completed,
      end: total,
    };
  }

  if (goal.metric?.id === DefaultMetricOptions.SubGoalProgress) {
    const subGoals = getSubGoals(goal.id, { goals, recursive: true });

    return {
      start: 0,
      current: subGoals.filter(({ completedAt }) => !!completedAt).length,
      end: subGoals.length,
    };
  }

  const start = goal.metric?.startValue ?? 0;
  const lastEntry = goal.metric.entries
    ?.slice()
    .sort((entryA, entryB) => entryB.date.getTime() - entryA.date.getTime())[0];
  const current = lastEntry
    ? calculateMetricEntryValue(lastEntry.id, goal.metric.entries!, start)
    : start;

  return {
    start,
    end: goal.metric?.targetValue ?? Infinity,
    current,
  };
};
